import { useMutation, useQuery } from "@tanstack/react-query"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"
import { FECHA_MINIMA } from "@/lib/fechas"

import type { Objetivo } from "./tipos"

/**
 * Crear o editar un objetivo trazable.
 *
 * ⚠ **El indicador y la meta no son adorno: son lo que distingue un objetivo de una
 * intención.** «Mejorar el servicio» no se puede comprobar; «% de despachos liberados
 * en menos de 5 días, de 62 a 85» sí. El formulario los pide juntos por eso.
 *
 * ⚠ **El responsable es un CARGO, no un nombre.** Es la misma decisión que en los
 * pasos de un proceso: con texto libre, «Gerencia de Operaciones» y «Gcia.
 * Operaciones» son dos responsables distintos y el seguimiento deja de agrupar. Y
 * cuando la persona cambia, el objetivo sigue teniendo dueño.
 */

type Cargo = { id: number; nombre: string }

export function FormularioObjetivo({
  pilarId,
  objetivo,
  onCerrar,
  onGuardado,
}: {
  pilarId: number
  objetivo: Objetivo | null
  onCerrar: () => void
  onGuardado: () => void
}) {
  const [campos, setCampos] = useState({
    enunciado: objetivo?.enunciado ?? "",
    indicador: objetivo?.indicador ?? "",
    linea_base: objetivo?.linea_base ?? "",
    meta: objetivo?.meta ?? "",
    unidad: objetivo?.unidad ?? "",
    responsable_cargo_id: objetivo?.responsable_cargo_id ?? null,
    fecha_inicio: objetivo?.fecha_inicio ?? "",
    fecha_fin: objetivo?.fecha_fin ?? "",
  })

  const cargos = useQuery({
    queryKey: ["cargos", "activos"],
    queryFn: () => api.get<Cargo[]>("/api/v1/cargos?solo_activos=true"),
  })

  const cuerpo = () => ({
    enunciado: campos.enunciado,
    indicador: campos.indicador || null,
    linea_base: campos.linea_base === "" ? null : campos.linea_base,
    meta: campos.meta === "" ? null : campos.meta,
    unidad: campos.unidad || null,
    responsable_cargo_id: campos.responsable_cargo_id,
    fecha_inicio: campos.fecha_inicio || null,
    fecha_fin: campos.fecha_fin || null,
  })

  const guardar = useMutation({
    mutationFn: () =>
      objetivo
        ? api.patch(`/api/v1/estrategia/objetivos/${objetivo.id}`, cuerpo())
        : api.post(`/api/v1/estrategia/pilares/${pilarId}/objetivos`, cuerpo()),
    onSuccess: () => {
      onGuardado()
      onCerrar()
    },
  })

  const estilo = {
    borderColor: "var(--border)",
    backgroundColor: "var(--surface)",
    color: "var(--fg)",
  }

  return (
    <Modal
      abierto
      titulo={objetivo ? "Editar el objetivo" : "Nuevo objetivo"}
      descripcion="Un objetivo sin indicador ni meta no se puede comprobar."
      onCerrar={onCerrar}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="o-enunciado" className="mb-1 block text-xs font-medium uppercase">
            Enunciado
          </label>
          <input
            id="o-enunciado"
            value={campos.enunciado}
            onChange={(e) => setCampos({ ...campos, enunciado: e.target.value })}
            maxLength={300}
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
            style={estilo}
          />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="o-indicador" className="mb-1 block text-xs font-medium uppercase">
            Indicador de cumplimiento
          </label>
          <input
            id="o-indicador"
            value={campos.indicador}
            onChange={(e) => setCampos({ ...campos, indicador: e.target.value })}
            placeholder="% de despachos liberados en menos de 5 días"
            maxLength={300}
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
            style={estilo}
          />
        </div>

        <div>
          <label htmlFor="o-base" className="mb-1 block text-xs font-medium uppercase">
            Línea base
          </label>
          <input
            id="o-base"
            type="number"
            step="0.0001"
            value={campos.linea_base}
            onChange={(e) => setCampos({ ...campos, linea_base: e.target.value })}
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm tabular"
            style={estilo}
          />
        </div>

        <div>
          <label htmlFor="o-meta" className="mb-1 block text-xs font-medium uppercase">
            Meta
          </label>
          <input
            id="o-meta"
            type="number"
            step="0.0001"
            value={campos.meta}
            onChange={(e) => setCampos({ ...campos, meta: e.target.value })}
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm tabular"
            style={estilo}
          />
        </div>

        <div>
          <label htmlFor="o-unidad" className="mb-1 block text-xs font-medium uppercase">
            Unidad
          </label>
          <input
            id="o-unidad"
            value={campos.unidad}
            onChange={(e) => setCampos({ ...campos, unidad: e.target.value })}
            placeholder="%"
            maxLength={40}
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
            style={estilo}
          />
        </div>

        <div>
          <label htmlFor="o-responsable" className="mb-1 block text-xs font-medium uppercase">
            Responsable
          </label>
          <select
            id="o-responsable"
            value={campos.responsable_cargo_id ?? ""}
            onChange={(e) =>
              setCampos({
                ...campos,
                responsable_cargo_id: e.target.value ? Number(e.target.value) : null,
              })
            }
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm"
            style={estilo}
          >
            <option value="">Sin asignar</option>
            {cargos.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="o-inicio" className="mb-1 block text-xs font-medium uppercase">
            Desde
          </label>
          <input
            id="o-inicio"
            type="date"
            min={FECHA_MINIMA}
            value={campos.fecha_inicio}
            onChange={(e) => setCampos({ ...campos, fecha_inicio: e.target.value })}
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm tabular"
            style={estilo}
          />
        </div>

        <div>
          <label htmlFor="o-fin" className="mb-1 block text-xs font-medium uppercase">
            Hasta
          </label>
          <input
            id="o-fin"
            type="date"
            min={FECHA_MINIMA}
            value={campos.fecha_fin}
            onChange={(e) => setCampos({ ...campos, fecha_fin: e.target.value })}
            className="h-9 w-full rounded-[var(--radius-control)] border px-2 text-sm tabular"
            style={estilo}
          />
        </div>
      </div>

      {guardar.isError && (
        <p role="alert" className="mt-4 text-sm" style={{ color: "var(--destructive)" }}>
          {guardar.error instanceof ErrorApi ? guardar.error.message : "No se pudo guardar"}
        </p>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <Boton variante="fantasma" onClick={onCerrar}>
          Cancelar
        </Boton>
        <Boton
          variante="primario"
          onClick={() => guardar.mutate()}
          disabled={!campos.enunciado.trim() || guardar.isPending}
        >
          {guardar.isPending ? "Guardando…" : "Guardar"}
        </Boton>
      </div>
    </Modal>
  )
}
